import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PerioExamDirection, PerioExamDirectionCardComponent } from '../perio-exam-direction-card/perio-exam-direction-card.component';
import { PerioExamCarouselButtonComponent } from '../perio-exam-carousel-button/perio-exam-carousel-button.component';
import { PerioExamTraversalStrategy } from '../../../perio-exam-screen/services/perio-exam/models';

const SLIDE_GAP = 16;

@Component({
  selector: 'lib-perio-exam-direction-carousel',
  imports: [CommonModule, PerioExamDirectionCardComponent, PerioExamCarouselButtonComponent],
  templateUrl: './perio-exam-direction-carousel.component.html',
  styleUrl: './perio-exam-direction-carousel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioExamDirectionCarouselComponent implements AfterViewInit {
  private readonly renderer = inject(Renderer2);

  public readonly perioExamDirections = input.required<PerioExamDirection[]>();
  public readonly selectedPerioExamDirectionId = input<PerioExamTraversalStrategy>();
  public readonly perioExamDirectionSelected = output<PerioExamTraversalStrategy>();

  private readonly carouselItems = viewChildren(PerioExamDirectionCardComponent, { read: ElementRef });
  private readonly carouselContainer = viewChild<ElementRef<HTMLDivElement>>('carouselContainer');

  public readonly activeCarouselItem = signal<number>(0);
  public readonly selectedCarouselItem = signal<number>(0);
  public readonly isNextButtonVisible = computed(() => this.activeCarouselItem() < this.carouselItems().length - 1);
  public readonly isBackButtonVisible = computed(() => this.activeCarouselItem() > 0);

  constructor() {
    effect(() => {
      this.focusCarouselItem(this.activeCarouselItem());
    });
  }

  public ngAfterViewInit(): void {
    const selectedId = this.selectedPerioExamDirectionId();
    if (selectedId) {
      this.setActiveCarouselItem(this.getCarouselItemIndex(selectedId));
      this.setSelectedCarouselItem(selectedId);
    }
    this.focusCarouselItem(this.activeCarouselItem());
  }

  protected moveToPreviousItem(): void {
    const previousIndex = this.activeCarouselItem() - 1;
    if (previousIndex >= 0) {
      this.setActiveCarouselItem(previousIndex);
    }
  }

  public moveToNextItem(): void {
    const nextIndex = this.activeCarouselItem() + 1;
    if (nextIndex < this.carouselItems().length) {
      this.setActiveCarouselItem(nextIndex);
    }
  }

  protected onCardClicked(direction: PerioExamDirection): void {
    this.setSelectedCarouselItem(direction.id!);
    this.perioExamDirectionSelected.emit(direction.id!);
  }

  private getCarouselItemIndex(id: PerioExamTraversalStrategy): number {
    return this.perioExamDirections()?.findIndex((d) => d.id === id) ?? 0;
  }

  private setActiveCarouselItem(index: number): void {
    this.activeCarouselItem.set(index);
  }

  private setSelectedCarouselItem(id: PerioExamTraversalStrategy): void {
    this.selectedCarouselItem.set(this.getCarouselItemIndex(id));
  }

  private calculateSlidePositions(targetSlide: number, carouselContainer: HTMLDivElement, carouselItem: HTMLElement) {
    const carouselWidth = carouselContainer.offsetWidth;
    const itemWidth = carouselItem.offsetWidth;
    const carouselCenter = carouselWidth / 2;
    const carouselEdgeOffset = itemWidth / 2;

    return carouselCenter - targetSlide * (itemWidth + SLIDE_GAP) - carouselEdgeOffset;
  }

  private focusCarouselItem(index: number = 0): void {
    const items = this.carouselItems();
    const carouselContainer = this.carouselContainer();

    if (items.length === 0 || !carouselContainer || index < 0 || index >= items.length) {
      return;
    }

    const offset = this.calculateSlidePositions(index, carouselContainer.nativeElement, items[index].nativeElement);

    this.renderer.setStyle(carouselContainer.nativeElement, 'transform', `translateX(${offset}px)`);
  }
}
